import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { trackEvent } from '../../../../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface LanguageUrl {
  locale: 'en' | 'fi' | 'sv';
  url: string;
}

export interface LanguageSwitcherProps {
  languageUrls: {
    all: LanguageUrl[];
  };
  canonicalLocale: string;
  originalJobLanguage?: string | null;
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  languageUrls,
  canonicalLocale,
  originalJobLanguage,
  className,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  if (!languageUrls || languageUrls.all.length <= 1) {
    return null;
  }

  const localeNames: Record<'en' | 'fi' | 'sv', string> = {
    en: 'English',
    fi: 'Suomi',
    sv: 'Svenska'
  };

  const originalLanguageLabel = formatMessage({
    id: 'jobDetailPage.originalLanguage',
    defaultMessage: 'Original',
    description: 'Label indicating the original language the job was posted in',
  });

  // py-1.5 keeps the tap target close to the ~24px WCAG 2.5.8 minimum even at the compact mobile text size.
  const pillBaseClassName =
    'shrink-0 rounded-full px-2 py-1.5 text-[11px] sm:px-2.5 sm:py-1.5 sm:text-xs font-medium leading-none transition-colors';

  return (
    <nav
      className={className ?? 'mb-4 px-1 sm:px-0'}
      aria-label={formatMessage({
        id: 'jobDetailPage.languageNavigation',
        defaultMessage: 'Available languages',
        description: 'Accessibility label for language selector showing available translations of the job'
      })}
    >
      <div className="flex flex-nowrap items-center gap-1 text-xs sm:gap-2 sm:text-sm">
        <span className="hidden shrink-0 text-[#3D3529] dark:text-[#C4A882] sm:inline">
          <Msg
            id="jobDetailPage.availableLanguages"
            defaultMessage="Available in:"
            description="Label prefix for list of languages this job is available in"
          />
        </span>
        <div className="inline-flex flex-nowrap items-center gap-0.5 rounded-full border border-[#D9CBB3] p-0.5 dark:border-[#4A3F30]">
          {languageUrls.all.map(({ locale, url }) => {
            const isCurrent = locale === canonicalLocale;
            const isOriginal = originalJobLanguage && locale === originalJobLanguage;
            const title = isOriginal ? `${localeNames[locale]} (${originalLanguageLabel})` : localeNames[locale];

            return isCurrent ? (
              <span
                key={locale}
                className={`${pillBaseClassName} relative bg-[#3B5E45] text-white dark:bg-[#98CFA8] dark:text-[#1E1A14]`}
                title={title}
              >
                {localeNames[locale]}
                {isOriginal && (
                  <span
                    className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#C9A24B] dark:bg-[#F2D896]"
                    aria-hidden="true"
                  />
                )}
                {isOriginal && <span className="sr-only"> ({originalLanguageLabel})</span>}
              </span>
            ) : (
              <a
                key={locale}
                href={url}
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent({
                    name: 'locale_change',
                    params: {
                      to_locale: locale,
                      from_locale: canonicalLocale,
                      surface: 'job_detail',
                    },
                  });
                  window.location.href = url;
                }}
                title={title}
                className={`${pillBaseClassName} relative text-[#7A6E5F] hover:bg-[#3B5E45]/10 hover:text-[#2A4533] dark:text-[#B8A48C] dark:hover:bg-[#98CFA8]/10 dark:hover:text-[#B6D4BC]`}
              >
                {localeNames[locale]}
                {isOriginal && (
                  <span
                    className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#C9A24B] dark:bg-[#F2D896]"
                    aria-hidden="true"
                  />
                )}
                {isOriginal && <span className="sr-only"> ({originalLanguageLabel})</span>}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
